import Head from "next/head";
import Link from "next/link";
import CaseStudyThumbnail from "@/components/case-studies/case-study-thumbnail";
import { ArrowUpRight } from "lucide-react";
import MetaTags from "@/components/MetaTags";
import PageHeader from "@/components/page-header";
import BookButton from "@/components/ui/BookButton";
import { caseStudyPages } from "@/constants/case-study-pages";

export default function CaseStudiesPage() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Ecommerce Case Studies",
    url: "https://www.venturawebdesign.com/case-studies",
    mainEntity: {
      "@type": "ItemList",
      itemListElement: caseStudyPages.map((study, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: study.title,
        url: `https://www.venturawebdesign.com${study.path}`,
      })),
    },
  };

  return (
    <>
      <MetaTags
        pageTitle="Ecommerce Case Studies"
        pageDescription="Explore Ventura’s ecommerce case studies, covering Shopify migrations, storefront support, speed, accessibility, and measurable marketing results."
        path="/case-studies"
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      </Head>
      <div className="bg-white text-black">
        <PageHeader
          title="Case studies"
          description="A closer look at the stores we support and the problems we help solve."
        />
        <div className="mx-auto max-w-screen-xl px-5 md:px-10">
          <div className="mx-auto max-w-5xl space-y-10 sm:space-y-12">
            {caseStudyPages.map((study) => (
              <article
                key={study.slug}
                className="grid items-center gap-6 md:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] md:gap-10"
              >
                <Link
                  href={study.path}
                  aria-label={`Read the ${study.client} case study`}
                  className="group block overflow-hidden rounded-3xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600"
                >
                  <CaseStudyThumbnail study={study} compact />
                </Link>
                <div>
                  <h2 className="text-xl font-bold leading-tight tracking-tight sm:text-2xl">
                    <Link href={study.path} className="hover:text-vblue3">
                      {study.title}
                    </Link>
                  </h2>
                  <p className="mt-4 text-base leading-7 text-black">
                    {study.description}
                  </p>
                  <Link
                    href={study.path}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-vblue3 hover:underline"
                  >
                    Read the case study{" "}
                    <ArrowUpRight aria-hidden="true" className="size-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <section className="py-20 text-center sm:py-28">
            <div
              aria-hidden="true"
              className="mx-auto mb-12 h-1 w-16 rounded-full bg-vblue3"
            />
            <h2 className="text-3xl font-bold tracking-tight">
              Let us talk about your project.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-8">
              Tell us what you want to improve. We can help define the work and
              a practical way to measure progress.
            </p>
            <div className="mt-8">
              <BookButton text="Book a free call" color="blue" />
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

CaseStudiesPage.serviceTheme = "blue";
